import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../Modal.tsx';
import { Button, Field, SelectField } from '../ui.tsx';
import { MediaThumb } from '../MediaThumb.tsx';
import { useScreens } from '../../api/screens.ts';
import { useMedia } from '../../api/media.ts';
import { useBrand } from '../../api/brand.ts';
import { useStartBroadcast } from '../../api/broadcast.ts';
import { ApiError } from '../../api/client.ts';

const MAX_MESSAGE = 200;

// Duration options → duration_minutes (undefined = until cancelled).
const DURATIONS: Array<{ label: string; minutes?: number }> = [
  { label: 'Until cancelled' },
  { label: '30 minutes', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '4 hours', minutes: 240 },
];

/** The announcement slide, approximated with the org's brand colors. Labeled
 *  "Preview" — it stands in for what the fleet will show, not a pixel-true render. */
function AnnouncementPreview({
  message,
  mediaId,
}: {
  message: string;
  mediaId: string | null;
}) {
  const brand = useBrand();
  const media = useMedia({ kind: 'image' });
  const colors = brand.data?.colors ?? [];
  const bg = colors[1] ?? colors[0] ?? '#14264a';
  const accent = colors[0] ?? '#2f6bff';
  const picked = mediaId ? (media.data ?? []).find((m) => m.id === mediaId) : undefined;

  return (
    <figure className="bcast-preview" aria-label="Announcement preview">
      <div className="bcast-preview-stage" style={{ background: bg }}>
        {picked ? (
          <MediaThumb media={picked} className="bcast-preview-media" />
        ) : (
          <>
            <span className="bcast-preview-bar" style={{ background: accent }} aria-hidden />
            <p className="bcast-preview-msg">{message.trim() || 'Your announcement…'}</p>
          </>
        )}
      </div>
      <figcaption className="bcast-preview-label">Preview</figcaption>
    </figure>
  );
}

/**
 * Compose + confirm an instant org-wide broadcast. Two phases: 'compose' (message,
 * duration, optional existing slide, live preview) then 'confirm' (the takeover
 * warning with the real screen count). On success the parent closes; the org-wide
 * banner appears via the invalidated GET /broadcast/current query.
 */
export function BroadcastModal({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<'compose' | 'confirm'>('compose');
  const [message, setMessage] = useState('');
  const [durationIdx, setDurationIdx] = useState(0);
  const [mediaId, setMediaId] = useState<string | null>(null);
  const [showMedia, setShowMedia] = useState(false);

  const screens = useScreens();
  const images = useMedia({ kind: 'image' });
  const start = useStartBroadcast();

  const screenCount = screens.data?.length ?? 0;
  const trimmed = message.trim();
  const remaining = MAX_MESSAGE - message.length;
  // A broadcast needs a message; a slide alone still wants a caption for readouts.
  const canReview = trimmed.length > 0 && !start.isPending;

  const durationMinutes = useMemo(() => DURATIONS[durationIdx]?.minutes, [durationIdx]);

  function submit() {
    start.mutate(
      {
        message: trimmed,
        ...(mediaId ? { media_id: mediaId } : {}),
        ...(durationMinutes ? { duration_minutes: durationMinutes } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(
            `Broadcasting to ${res.screens_total} screen${res.screens_total === 1 ? '' : 's'}` +
              (res.delivered < res.screens_total ? ` · ${res.delivered} live now` : ''),
          );
          onClose();
        },
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : 'Could not start the broadcast'),
      },
    );
  }

  return (
    <Modal
      title={phase === 'compose' ? 'Broadcast to all screens' : 'Confirm broadcast'}
      onClose={onClose}
      size="lg"
      footer={
        phase === 'compose' ? (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => setPhase('confirm')} disabled={!canReview}>
              Review
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setPhase('compose')} disabled={start.isPending}>
              Back
            </Button>
            <Button variant="danger" onClick={submit} disabled={start.isPending}>
              {start.isPending ? 'Starting…' : 'Start broadcast'}
            </Button>
          </>
        )
      }
    >
      {phase === 'compose' ? (
        <div className="bcast-compose">
          <Field label="Message" hint={`${trimmed.length ? remaining : MAX_MESSAGE} characters left`}>
            <textarea
              className="bcast-textarea"
              value={message}
              maxLength={MAX_MESSAGE}
              rows={3}
              autoFocus
              placeholder="e.g. Store closing early today at 4pm — thanks for visiting!"
              onChange={(e) => setMessage(e.target.value)}
              aria-label="Broadcast message"
            />
          </Field>

          <SelectField
            label="Duration"
            value={String(durationIdx)}
            onChange={(e) => setDurationIdx(Number(e.target.value))}
          >
            {DURATIONS.map((d, i) => (
              <option key={d.label} value={i}>
                {d.label}
              </option>
            ))}
          </SelectField>

          <div className="bcast-advanced">
            <button
              type="button"
              className="bcast-advanced-toggle"
              aria-expanded={showMedia}
              onClick={() => setShowMedia((v) => !v)}
            >
              {showMedia ? 'Hide slide options' : 'Use an existing slide (optional)'}
            </button>
            {showMedia && (
              <div className="bcast-media-grid">
                <button
                  type="button"
                  className={`bcast-media-opt${mediaId === null ? ' on' : ''}`}
                  onClick={() => setMediaId(null)}
                >
                  Text only
                </button>
                {(images.data ?? []).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`bcast-media-opt${mediaId === m.id ? ' on' : ''}`}
                    onClick={() => setMediaId(m.id)}
                    title={m.filename ?? undefined}
                  >
                    <MediaThumb media={m} className="bcast-media-thumb" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <AnnouncementPreview message={message} mediaId={mediaId} />
        </div>
      ) : (
        <div className="bcast-confirm">
          <div className="hint-box warn">
            <strong>This takes over ALL {screenCount} screens immediately.</strong>
            <p className="mt-2">
              Every screen switches to this announcement now, on top of its schedule.
              {durationMinutes
                ? ` It clears automatically after ${DURATIONS[durationIdx]!.label.toLowerCase()}.`
                : ' It stays until you cancel it.'}
            </p>
          </div>
          <div className="mt-4">
            <AnnouncementPreview message={message} mediaId={mediaId} />
          </div>
        </div>
      )}
    </Modal>
  );
}
